import { Loader } from '@googlemaps/js-api-loader';
import { onMounted, Ref, ref } from 'vue';
import {
  ILocationInfo,
  addYourLocationButton,
  getLocationByIP,
  getLocationInfo,
  getLocation as getLocationRaw,
  zoomControl,
} from 'src/utils/map';
import { checkTheme, config } from 'src/utils/config';
import { loadScript } from 'src/utils/helpers';
import { isLoqateEnabled, searchAddress } from 'src/utils/loqate';
import { LoqateSuggestion } from 'src/types/loqate';

let loaderInstance: Loader | null = null;
let loadPromise: Promise<unknown> | null = null;

export function initializeApis() {
  if (loadPromise) {
    return loadPromise;
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Cannot initialize APIs in non-browser environment.'));
  }

  loaderInstance = new Loader({
    apiKey: env.VITE_APP_GMAPS_KEY,
    version: 'quarterly',
    libraries: ['places', 'maps', 'marker', 'geocoding'],
  });

  loadPromise = Promise.allSettled([
    loaderInstance.importLibrary('places'),
    loaderInstance.importLibrary('maps'),
    loaderInstance.importLibrary('marker'),
    loaderInstance.importLibrary('geocoding'),
    loadW3w(),
  ]);

  return loadPromise;
}

export async function loadW3w() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    console.warn('Cannot load W3W script in non-browser environment.');
    return Promise.resolve(false);
  }
  try {
    if (!env.W3W_API_KEY) {
      console.warn('No W3W_API_KEY found');
      return false;
    }
    if (document.getElementById('w3w-script')) {
      return !!window.what3words;
    }
    await loadScript(`https://assets.what3words.com/sdk/v3/what3words.js?key=${env.W3W_API_KEY}`, {
      id: 'w3w-script',
    });
    return true;
  } catch {
    return false;
  }
}
const w3wCache: { latLng?: google.maps.LatLngLiteral; result: CoordinateResponse | undefined } = {
  latLng: undefined,
  result: undefined,
};

/**
 * Converts a coordinate to a 3 word address
 * @param coordinates - The coordinate object
 * @param coordinates.lat - The latitude
 * @param coordinates.lng - The longitude
 * @param [language='en'] - The language to return the 3 word address in
 * @returns Promise 3 word address response
 */
export async function convertTo3wa(coordinates: LatLng, language = 'en'): Promise<CoordinateResponse | undefined> {
  if (typeof window === 'undefined' || !window.what3words?.api) {
    console.warn('W3W API not available.');
    return Promise.resolve(undefined);
  }
  try {
    await initializeApis();
    if (!env.W3W_API_KEY && !window?.what3words?.api) {
      console.warn('No W3W_API_KEY found');
      return;
    }
    const coords = coordinates.toJSON();
    if (w3wCache?.latLng?.lat === coords?.lat) {
      if (w3wCache?.result) {
        return w3wCache.result;
      }
    }
    // @ts-expect-error no types
    const result = window.what3words.api.convertTo3wa(coords, language) as CoordinateResponse;
    w3wCache.latLng = coords;
    w3wCache.result = result;
    return result;
  } catch (error) {
    return undefined;
  }
}

/**
 * Place data type
 */
export interface CoordinateResponse {
  country: string;
  southwest: {
    lng: number;
    lat: number;
  };
  northeast: {
    lng: number;
    lat: number;
  };
  nearestPlace: string;
  coordinates: {
    lng: number;
    lat: number;
  };
  words: string;
  language: string;
  map: string;
}

export function createPin(primary: string | undefined) {
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    return null;
  }
  let svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" style="--icon-color:${primary || 'black'};scale: 1.5" viewBox="0 0 18 24" width="18" height="24">
      <path
        clip-rule="nonzero"
        fill-rule="nonzero"
        d="M9.7685 23.0866C9.7296 23.1333 9.6866 23.1763 9.6399 23.2152C9.2154 23.5686 8.5849 23.511 8.2315 23.0866C2.74384 16.4959 0 11.6798 0 8.63811C0 3.86741 4.2293 0 9 0C13.7707 0 18 3.86741 18 8.63811C18 11.6798 15.2562 16.4959 9.7685 23.0866zM9 12C10.6569 12 12 10.6569 12 9C12 7.34315 10.6569 6 9 6C7.3431 6 6 7.34315 6 9C6 10.6569 7.3431 12 9 12z"
        fill="var(--icon-color, currentColor)"
      />
    </svg>
  `;
  if (checkTheme('bentley')) {
    svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="none" viewBox="0 0 16 16" style="--icon-color:${primary || 'black'}">
        <path
          fill="var(--icon-color, currentColor)"
          fill-rule="evenodd"
          d="M8 10c1.587 0 3.051-.377 4.226-1.014-.478 1.118-1.226 2.078-2.006 2.859A13.385 13.385 0 0 1 8 13.625a13.383 13.383 0 0 1-2.22-1.78c-.78-.78-1.528-1.741-2.006-2.858C4.95 9.622 6.413 10 8 10ZM1.75 6.5c0 .278.015.55.044.815.26 2.398 1.63 4.294 2.926 5.59a14.878 14.878 0 0 0 2.653 2.097 9.189 9.189 0 0 0 .245.144l.015.008.005.003h.002v.001L8 14.5l-.36.658a.75.75 0 0 0 .72 0L8 14.5l.36.658.002-.001.005-.003.015-.008A4.693 4.693 0 0 0 8.627 15a14.873 14.873 0 0 0 2.653-2.096c1.296-1.296 2.666-3.192 2.926-5.59l.005-.048a7.54 7.54 0 0 0 .039-.767C14.25 3.086 11.414.25 8 .25S1.75 3.086 1.75 6.5Zm9.062-1.137a.436.436 0 0 1 .008.005v.002c.383.227 1.19.758 1.18 1.584-.017 1.375-1.753 2.087-3.967 2.044a7.029 7.029 0 0 1-3.574-.918l-.05-.034c-.151-.103-.411-.28-.409-.343.002-.069.054-.113.112-.161.016-.014.033-.027.048-.042.08-.074.148-.137.21-.192.041-.038.08-.072.113-.104l.01-3.284s-.214-.201-.341-.308a.338.338 0 0 1-.15-.193c-.01-.094.273-.31.396-.405l.008-.006c.76-.59 2.102-.97 3.462-1.005 2.706-.068 3.877.92 3.92 1.819.036.8-.521 1.115-.91 1.336l-.03.017c-.083.047-.114.079-.111.103.004.045.047.07.075.085Zm-1.103-1.55C9.74 2.51 8.565 2.454 7.983 2.458c-.648.005-1.633.204-1.582 1.379.047 1.066.708 1.34 1.677 1.359.92.017 1.605-.302 1.63-1.383ZM6.356 7.07c.055 1.16.76 1.457 1.803 1.478.991.02 1.726-.328 1.754-1.503.036-1.416-1.229-1.477-1.856-1.473-.696.006-1.758.274-1.7 1.498Z"
          clip-rule="evenodd"
        ></path>
      </svg>
    `;
  }

  const parser = new DOMParser();
  const pinSvg = parser.parseFromString(svgString, 'image/svg+xml').documentElement;
  return pinSvg;
}

/**
 * createAnimatedMarker - creates an animated marker for a map
 * @param { google.maps.Map } map
 * @param { google.maps.LatLng } position
 * @returns google.maps.marker.AdvancedMarkerElement
 */
export async function createAnimatedMarker(
  map: google.maps.Map,
  position: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined,
  options = { isAnimated: true }
) {
  await initializeApis();
  const marker = new google.maps.marker.AdvancedMarkerElement({
    map,
    position,
    content: createPin(config.value?.theme?.primary),
  });
  if (options.isAnimated) {
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const content = marker.content as HTMLElement;
      if (content) {
        content.style.opacity = '0';
        content.classList.add('drop');
        content.addEventListener('animationend', () => {
          content.classList.remove('drop');
          content.style.opacity = '1';
        });
      }
    });
  }
  map.addListener('bounds_changed', () => {
    marker.position = map.getCenter();
  });
  return marker;
}

interface MapsInternalOptions {
  marker: boolean;
}

interface PlaceChangeOptions {
  zoom?: number;
}

export function useGoogleMaps(
  componentOrSelector: string | Ref<HTMLElement | undefined> | HTMLElement,
  onPlaceChanged?: (place: ILocationInfo) => void
) {
  let map: google.maps.Map;
  let marker: google.maps.marker.AdvancedMarkerElement;
  let listener: google.maps.MapsEventListener;
  const isMapsLoaded = ref(false);
  const defaultLocation = { latitude: 51.51160940068564, longitude: -0.08719166730719596 };
  const isGeolocationAllowed = ref(false);
  const defaultZoom = ref(16);

  const getElement = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('Cannot get element in non-browser environment.');
    }
    const error = new Error('Can not load map, no element or selector provided');
    if (typeof window === 'undefined') throw error;
    if (!componentOrSelector) {
      throw error;
    }

    if (typeof componentOrSelector === 'string') {
      const elem = document.getElementById(componentOrSelector);
      if (!elem) {
        throw error;
      }
      return elem;
    }

    if (componentOrSelector instanceof HTMLElement) {
      return componentOrSelector;
    }

    if (!componentOrSelector.value) {
      throw error;
    }
    return componentOrSelector.value;
  };

  async function waitComplete() {
    await initializeApis();
    isMapsLoaded.value = true;
  }

  async function load(opts: google.maps.MapOptions, internalOptions: MapsInternalOptions = { marker: true }) {
    await waitComplete();
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('Cannot load map in non-browser environment.');
    }
    const element = getElement();

    map = new google.maps.Map(element, {
      zoom: defaultZoom.value,
      disableDefaultUI: true,
      mapId: 'axi-map',
      gestureHandling: 'greedy',
      ...opts,
    });
    if (internalOptions?.marker) {
      marker = await createAnimatedMarker(map, opts?.center, { isAnimated: false });
      marker.position = map.getCenter();
    }

    const zoomControlDiv = document?.createElement?.('div');
    if (zoomControlDiv) {
      zoomControl(zoomControlDiv, map);
      map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControlDiv);
    }

    if (isGeolocationAllowed.value) {
      const locationControlDiv = document?.createElement?.('div');
      if (locationControlDiv) {
        addYourLocationButton(locationControlDiv, map);
        map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(locationControlDiv);
      }
    }

    // setTimeout(async () => {
    //   if (opts?.center) {
    //     const newLocation = await getLocationInfo(new google.maps.LatLng(opts?.center));
    //     if (newLocation?.latitude && onPlaceChanged) onPlaceChanged(newLocation);
    //   }
    // }, 200);

    listener = map.addListener('zoom_changed', findLocation);

    map.addListener('dragend', findLocation);
    return map;
  }

  async function findLocation() {
    const center = map?.getCenter();
    marker && (marker.position = center);
    if (!center) return;
    const newLocation = await getLocationInfo(center);
    if (newLocation?.latitude && onPlaceChanged) onPlaceChanged(newLocation);
  }

  async function loadPreview(opts: google.maps.MapOptions) {
    await waitComplete();
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('Cannot load map preview in non-browser environment.');
    }
    const element = getElement();

    map = new google.maps.Map(element, {
      zoom: defaultZoom.value,
      disableDefaultUI: true,
      disableDoubleClickZoom: true,
      gestureHandling: 'none',
      keyboardShortcuts: false,
      clickableIcons: false,
      mapId: 'axi-map-preview',
      ...opts,
    });
    marker = await createAnimatedMarker(map, opts?.center);
    setTimeout(() => {
      marker && (marker.position = map?.getCenter());
    }, 200);
  }

  async function getLocation() {
    const [location, zoom, geoLocation] = await getLocationRaw(defaultZoom.value, defaultLocation);
    defaultZoom.value = zoom;
    isGeolocationAllowed.value = geoLocation;
    return location;
  }

  function placeChanged(place: ILocationInfo, options: PlaceChangeOptions = { zoom: 16 }) {
    if (typeof window === 'undefined' || !google?.maps) return;
    if (place?.latitude && place?.longitude) {
      map?.panTo(new google.maps.LatLng(place.latitude, place.longitude));
      google.maps.event.removeListener(listener);
      if (options?.zoom) {
        map?.setZoom(options?.zoom || defaultZoom.value);
      }
      listener = map?.addListener('zoom_changed', findLocation);
    }
  }

  return {
    load,
    loadPreview,
    getLocation,
    placeChanged,
    loadPromise,
  };
}

const regexPatterns: Record<string, RegExp> = {
  number: /\d+/g,
  degree: /^([ns]?)\s*([-+]?\d+\.?\d*)\s*°?\s*([ns]?)\s*,?\s+([eow]?)\s*([-+]?\d+\.?\d*)\s*°?\s*([eow]?)$/i,
  dms: /^(\d{1,3})\s*°\s*([\d.]+)\s*['′']\s*([\d.]*)?\s*[″"]?\s*([sn])[\s,]+(\d{1,3})\s*°\s*([\d.]+)\s*['′']\s*([\d.]*)?\s*[″"]?\s*([eow])$/i,
  w3wLooseAddress: RegExp(
    "^(\\D+)([.｡。･・︒។៖। ,\\\\^_/+ ':;=|　-]+)(\\D+)([.｡।･・︒។ ,\\\\^_/+ ':;=|　-]+)(\\D+)$",
    'u'
  ),
  w3wUnsupportedDelimiters: /[,-_/ ]+/g,
};

const isW3wLoaded = ref<boolean>(false);

export function useAddressSearch({ useLoqate = false } = {}) {
  const isLoading = ref(false);
  const isSearching = ref(false);

  function normalizeString(input: string): string {
    return input
      .toLowerCase()
      .replace(/^[.｡।･・︒។,-_/ ]+/, '')
      .replace(regexPatterns.w3wUnsupportedDelimiters, '.');
  }

  function checkDegreeOrDms(input: string): boolean {
    return regexPatterns.degree.test(input) || regexPatterns.dms.test(input);
  }

  function checkW3wLooseAddress(input: string): boolean {
    return regexPatterns.w3wLooseAddress.test(input);
  }

  /**
   * Main search function
   * 1) If user typed coordinates (degree or DMS), convert to lat/lng + do W3W
   * 2) If user typed normal text, do Google Place + W3W
   */
  async function search(
    keyWords: string = ''
  ): Promise<(GooglePlaceResult | W3WSuggestion | LoqateSuggestion)[] | undefined> {
    try {
      if (typeof window === 'undefined') return Promise.resolve(undefined);
      await initializeApis();
      if (!keyWords) return;
      isSearching.value = true;
      if (checkDegreeOrDms(keyWords.trim())) {
        const latLng = convertStringToLatLng(keyWords);
        if (!latLng) return;

        const w3wAddress = await convertTo3wa(latLng);
        const placePredictions = await placeGeocodingLatLng(latLng);

        const formattedLatLng: GooglePlaceResult = {
          structured_formatting: { main_text: keyWords },
          latitude: latLng.lat(),
          longitude: latLng.lng(),
        };

        return [
          formattedLatLng,
          ...placePredictions
            .slice(0, 9)
            .map(formatResult)
            .filter((prediction) => !!prediction?.structured_formatting?.main_text),
          w3wAddress || undefined,
        ].filter(Boolean) as GooglePlaceResult[];
      }

      const results: (GooglePlaceResult | W3WSuggestion | LoqateSuggestion)[] = [];
      if (isLoqateEnabled && useLoqate) {
        const res = await searchAddress(keyWords);
        results.push(...res);
      } else {
        const [ipLocation] = await getLocationByIP();
        const placePredictions = await getPredictions(keyWords, {
          // types: ['premise', 'street_number', 'route', 'locality'],
          locationBias:
            ipLocation &&
            typeof ipLocation === 'object' &&
            'latitude' in ipLocation &&
            ipLocation.latitude &&
            'longitude' in ipLocation &&
            ipLocation.longitude
              ? new google.maps.LatLng(ipLocation?.latitude as number, ipLocation?.longitude as number)
              : undefined,
        });
        results.push(...placePredictions);
      }

      if (isW3wLoaded.value && checkW3wLooseAddress(keyWords)) {
        const cleaned = normalizeString(keyWords);
        const w3wRes = await autoSuggest(cleaned);
        if (w3wRes?.suggestions?.length) {
          results.push(...w3wRes.suggestions);
        }
      }

      return results;
    } finally {
      isSearching.value = false;
    }
  }

  onMounted(async () => {
    isLoading.value = true;
    try {
      await initializeApis();
      isW3wLoaded.value = !!window.what3words;
    } catch (error) {
      console.error('Error initializing APIs on mount:', error);
      isW3wLoaded.value = false;
    } finally {
      isLoading.value = false;
    }
  });

  return {
    isLoading,
    isSearching,
    search,
    normalizeString,
    checkDegreeOrDms,
    checkW3wLooseAddress,
    getPlaceDetails,
    formatResult,
    convertToCoordinates,
    placeGeocodingLatLng,
  };
}

/**
 * Returns autosuggest results for a 3 word address input
 */
export async function autoSuggest(
  input: string,
  options?: Record<string, unknown>
): Promise<W3WAutoSuggestResponse | undefined> {
  if (typeof window === 'undefined' || !window.what3words?.api) return Promise.resolve(undefined);
  await initializeApis();
  return window.what3words.api.autosuggest(input, options);
}

/**
 * Returns coordinates for a 3 word address
 */
export async function convertToCoordinates(words: string): Promise<W3WCoordinateResponse | undefined> {
  if (typeof window === 'undefined' || !window.what3words?.api) return Promise.resolve(undefined);
  await initializeApis();
  return window.what3words.api.convertToCoordinates(words);
}

export function testRegex(pattern: string, input: string): RegExpMatchArray | null {
  const regex = RegExp(pattern, 'g');
  return input.match(regex);
}

/**
 * Reverse geocoding from lat/lng
 */
export async function placeGeocodingLatLng(latLng: LatLng): Promise<GoogleGeocoderResult[]> {
  if (typeof window === 'undefined' || !google?.maps?.Geocoder) return Promise.resolve([]);
  await initializeApis();
  const geocoder = new google.maps.Geocoder();
  const res = await geocoder.geocode({ location: latLng });
  return res?.results || [];
}

/**
 * Forward geocoding / predictions
 */
export async function getPredictions(
  input: string,
  options?: Partial<google.maps.places.AutocompletionRequest>
): Promise<GooglePlaceResult[]> {
  if (typeof window === 'undefined' || !google?.maps?.places?.AutocompleteService || !google?.maps?.Geocoder)
    return Promise.resolve([]);
  await initializeApis();
  const service = new google.maps.places.AutocompleteService();
  const places = await new Promise<google.maps.places.QueryAutocompletePrediction[] | null>((resolve) => {
    service.getQueryPredictions({ input, ...options }, (res) => {
      resolve(res);
    });
  });

  if (!places?.length) return [];
  const geocoder = new google.maps.Geocoder();
  const results = await Promise.all(
    places.slice(0, 9).map(async (placePrediction) => {
      if (!placePrediction?.place_id) return undefined;

      const result = await geocoder.geocode({ placeId: placePrediction.place_id });
      const first = result.results[0];
      if (!first) return undefined;

      // @ts-expect-error no types
      placePrediction.latitude = first.geometry.location.lat();
      // @ts-expect-error no types
      placePrediction.longitude = first.geometry.location.lng();
      // @ts-expect-error no types
      placePrediction.formatted_address = first.formatted_address;

      return placePrediction as GooglePlaceResult;
    })
  );

  return results.filter((r) => !!r?.structured_formatting?.main_text) as GooglePlaceResult[];
}

/**
 * Get place details by placeId
 */
export async function getPlaceDetails(
  placeId: string,
  options?: Partial<google.maps.places.PlaceDetailsRequest>
): Promise<google.maps.places.PlaceResult | undefined> {
  if (typeof window === 'undefined' || !google?.maps?.places?.PlacesService || typeof document === 'undefined')
    return Promise.resolve(undefined);
  await initializeApis();
  const dummyDiv = document.createElement('div');
  const service = new google.maps.places.PlacesService(dummyDiv);
  const res = await new Promise<google.maps.places.PlaceResult>((resolve) => {
    service.getDetails({ placeId, ...options }, (placeResult) => {
      if (!placeResult) return resolve({});
      resolve(placeResult);
    });
  });

  return res;
}

function convertStringToLatLng(inputString: string): LatLng | null {
  if (typeof window === 'undefined' || !google?.maps?.LatLng) return null;
  const degreeMatch = regexPatterns.degree.exec(inputString);
  if (degreeMatch) {
    let latitude = Number(degreeMatch[2]);
    let longitude = Number(degreeMatch[5]);

    if (/^[s]$/i.test(degreeMatch[1]) || /^[s]$/i.test(degreeMatch[3])) {
      latitude *= -1;
    }
    if (/^[wo]$/i.test(degreeMatch[4]) || /^[wo]$/i.test(degreeMatch[6])) {
      longitude *= -1;
    }

    return new google.maps.LatLng(latitude, longitude);
  }

  const dmsMatch = regexPatterns.dms.exec(inputString);
  if (dmsMatch) {
    const latDegrees = Number(dmsMatch[1]);
    const latMinutes = Number(dmsMatch[2]);
    const latSeconds = Number(dmsMatch[3]) || 0;
    const latDirection = dmsMatch[4].toLowerCase();

    const lngDegrees = Number(dmsMatch[5]);
    const lngMinutes = Number(dmsMatch[6]);
    const lngSeconds = Number(dmsMatch[7]) || 0;
    const lngDirection = dmsMatch[8].toLowerCase();

    if (
      latDegrees < 0 ||
      latDegrees > 89 ||
      latMinutes < 0 ||
      latMinutes > 59 ||
      latSeconds < 0 ||
      latSeconds > 179 ||
      lngDegrees < 0 ||
      lngDegrees > 179 ||
      lngMinutes < 0 ||
      lngMinutes > 59 ||
      lngSeconds < 0 ||
      lngSeconds > 179
    ) {
      return null;
    }

    let latitude = latDegrees + latMinutes / 60 + latSeconds / 3600;
    let longitude = lngDegrees + lngMinutes / 60 + lngSeconds / 3600;

    if (latDirection === 's') {
      latitude *= -1;
    }
    if (lngDirection === 'w' || lngDirection === 'o') {
      longitude *= -1;
    }

    return new google.maps.LatLng(latitude, longitude);
  }

  return null;
}

type LatLng = google.maps.LatLng;

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface GoogleGeocoderResult {
  address_components: AddressComponent[];
  formatted_address: string;
  geometry: {
    location: google.maps.LatLng;
    location_type?: string;
  };
  types?: string[];
}

export interface GooglePlaceResult extends Partial<GoogleGeocoderResult> {
  place_id?: string;
  latitude?: number;
  longitude?: number;
  structured_formatting?: {
    main_text: string;
    secondary_text?: string;
    main_text_matched_substrings?: { length: number; offset: number }[];
    secondary_text_matched_substrings?: { length: number; offset: number }[];
  };
}

interface W3WCoordinateResponse {
  country: string;
  square: {
    southwest: { lat: number; lng: number };
    northeast: { lat: number; lng: number };
  };
  nearestPlace: string;
  coordinates: { lat: number; lng: number };
  words: string;
  language: string;
  map: string;
}

export interface W3WSuggestion {
  country: string;
  nearestPlace: string;
  words: string;
  rank: number;
  language: string;
}

interface W3WAutoSuggestResponse {
  suggestions: W3WSuggestion[];
}

declare global {
  interface Window {
    what3words?: {
      api: {
        autosuggest: (input: string, options?: Record<string, unknown>) => Promise<W3WAutoSuggestResponse | undefined>;
        convertToCoordinates: (words: string) => Promise<W3WCoordinateResponse | undefined>;
        convertTo3wa: (
          coordinates: google.maps.LatLngLiteral,
          language: string
        ) => Promise<CoordinateResponse | undefined>;
      };
    };
  }
}

const ADDRESS_COMPONENTS: Record<string, 'short_name' | 'long_name'> = {
  subpremise: 'short_name',
  street_number: 'short_name',
  route: 'long_name',
  locality: 'long_name',
  administrative_area_level_1: 'long_name',
  administrative_area_level_2: 'long_name',
  country: 'long_name',
  postal_code: 'long_name',
  postal_town: 'long_name',
};

export function formatResult(place: GoogleGeocoderResult): GooglePlaceResult {
  const returnData: Record<string, unknown> = {};

  for (const component of place.address_components) {
    const addressType = component.types[0];
    if (ADDRESS_COMPONENTS[addressType]) {
      const key = ADDRESS_COMPONENTS[addressType];
      returnData[addressType] = component[key];
    }
  }

  let mainText = '';
  let secondaryText = '';

  if (returnData.route) {
    mainText = `${returnData.route}, ${returnData.street_number || ''}`;
    secondaryText = (returnData.locality ? `${returnData.locality}, ` : '') + (returnData.country || '');
  } else if (returnData.locality) {
    mainText = `${returnData.locality}`;
    secondaryText = `${returnData.country || ''}`;
  } else if (returnData.administrative_area_level_2) {
    mainText = `${returnData.administrative_area_level_2}, ${returnData.administrative_area_level_1}`;
    secondaryText = `${returnData.country || ''}`;
  } else if (returnData.administrative_area_level_1) {
    mainText = `${returnData.administrative_area_level_1}`;
    secondaryText = `${returnData.country || ''}`;
  } else {
    mainText = `${returnData.country || ''}`;
  }

  const formatted = {
    ...returnData,
    structured_formatting: {
      main_text: mainText,
      secondary_text: secondaryText,
    },
    latitude: place.geometry.location.lat(),
    longitude: place.geometry.location.lng(),
    formatted_address:
      ['ROOFTOP', 'RANGE_INTERPOLATED'].includes(place?.geometry?.location_type || '') ||
      !place?.types?.includes('plus_code')
        ? place.formatted_address
        : '',
  };

  return formatted as GooglePlaceResult;
}
